import styled from 'styled-components';
import { colorsV2 } from '@doltech/ui/lib/figma/colors-v2';
import { horizontalSpace } from '@doltech/utils/lib/css-style';
import * as React from 'react';
import { ResponsiveTypography } from '@doltech/ui/lib/figma/Typography/v3/ResponsiveTypography';
import { UserAvatar } from '@doltech/ui/lib/figma/User/UserAvatar/UserAvatar';
import { DotDivider } from '@doltech/ui/lib/figma/Divider/DotDivider';
import { CopyLinkButton } from './CopyLinkButton';
import { FacebookShareButton } from './FacebookShareButton';
import { useFromScreens } from '@doltech/ui/lib/hooks/useDeviceQuery.hook';
import { fromScreen } from '@doltech/core/lib/responsive/responsive.util';
import { getExerciseOrTheoryByTopicId } from '../../shared/utils';

const Container = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  ${horizontalSpace(12)};

  .avatar {
    &:hover + .blog-text {
      .author-name {
        border-color: ${colorsV2.gray180};
        width: max-content;
      }
    }
  }

  .author-name {
    border-bottom: 1px solid transparent;

    &:hover {
      border-color: ${colorsV2.gray180};
      width: max-content;
    }
  }

  .topic {
    border-bottom: 1px solid transparent;

    &:hover {
      border-color: ${colorsV2.gray180};
    }
  }
`;

const LinkWrap = styled.a``;

const TeacherCard = styled.div`
  display: flex;
  ${horizontalSpace(12)};
  align-items: flex-start;
`;
const TextWrapper = styled.div``;

const NameWrapper = styled.div`
  display: flex;
  align-items: center;
  ${horizontalSpace(8)};
`;

const ActionWrapper = styled.div`
  display: flex;
  align-items: center;
  ${horizontalSpace(4)};

  ${fromScreen(769)} {
    ${horizontalSpace(12)};
  }
`;

const Line = styled.div`
  height: 24px;
  width: 1px;
  border-right: 1px solid ${colorsV2.neutral20};
`;

interface ElementAuthorTopProps {
  pageId?: string;
  name?: string;
  avatarUrl?: string;
  date?: string;
  topic?: string;
  topicId?: string;
  facebookPage?: string;
  onCopyLink?: () => string;
  authorUrl?: string;
  topicUrl?: string;
  currentUrl?: string;
  disabledAuthor?: boolean;
  disabledTopic?: boolean;
}

export const ElementAuthorTop = (props: ElementAuthorTopProps) => {
  const {
    pageId,
    name,
    avatarUrl,
    date,
    topic,
    topicId,
    onCopyLink,
    authorUrl,
    topicUrl,
    currentUrl,
    disabledAuthor,
    disabledTopic,
  } = props;

  const [, from769] = useFromScreens([0, 769]);
  return (
    <Container>
      <TeacherCard>
        {disabledAuthor ?
          <UserAvatar
            className="avatar"
            layout="default"
            userName={name}
            userAvatar={avatarUrl}
            size={40}
          /> :
          <LinkWrap className="author-link" rel="author" href={authorUrl}>
            <UserAvatar
              className="avatar"
              layout="default"
              userName={name}
              userAvatar={avatarUrl}
              size={40}
            />
          </LinkWrap>
        }
        <TextWrapper className="blog-text">
          {disabledAuthor ?
            <ResponsiveTypography.Paragraph
              className="author-name"
              variant="semi-bold/14-20"
              color="gray180"
            >
              {name}
            </ResponsiveTypography.Paragraph> :
            <LinkWrap className="author-link" rel="author" href={authorUrl}>
              <ResponsiveTypography.Paragraph
                className="author-name"
                variant="semi-bold/14-20"
                color="gray180"
              >
                {name}
              </ResponsiveTypography.Paragraph>
            </LinkWrap>
          }
          <NameWrapper>
            <ResponsiveTypography.Paragraph variant="regular/14-20" color="gray120">
              {date}
            </ResponsiveTypography.Paragraph>
            <DotDivider dotSize={3} space={0} background={colorsV2.neutral40}/>
            {disabledTopic ?
              <ResponsiveTypography.Paragraph
                className="topic"
                variant="regular/14-20"
                color="gray180"
              >
                {topic}
              </ResponsiveTypography.Paragraph> :
              <LinkWrap className="topic-link" rel="nofollow" href={getExerciseOrTheoryByTopicId(topicUrl, topicId)}>
                <ResponsiveTypography.Paragraph
                  className="topic"
                  variant="regular/14-20"
                  color="gray180"
                >
                  {topic}
                </ResponsiveTypography.Paragraph>
              </LinkWrap>
            }
          </NameWrapper>
        </TextWrapper>
      </TeacherCard>
      {from769 ?
        <ActionWrapper>
          <CopyLinkButton onCopyLink={onCopyLink} hasText/>
          <Line/>
          <FacebookShareButton pageId={pageId} currentUrl={currentUrl} hasText/>
        </ActionWrapper> :
        <ActionWrapper>
          <CopyLinkButton onCopyLink={onCopyLink} hasText={false}/>
          <Line/>
          <FacebookShareButton pageId={pageId} currentUrl={currentUrl} hasText={false}/>
        </ActionWrapper>
      }
    </Container>
  );
};
